import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLine } from "@/components/site/Reveal";
import { waLink, WA_DEFAULT_MSG } from "@/lib/site";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy">
      <img
        src="/assets/brand/hero-spices.jpg"
        alt="Premium Indian spices and food ingredients in wooden bowls"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-navy/70" />

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-32 pt-36 md:px-10 md:pt-44">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-7 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.26em] text-gold md:text-xs"
        >
          <span className="h-px w-10 bg-gold" />
          RBL EXIM — Indian Food & FMCG Exporter · Rajkot, Gujarat
        </motion.p>

        <h1 className="max-w-4xl font-display text-[11.5vw] font-semibold leading-[1.02] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[5.2rem]">
          <MaskedLine delay={0.15}>Premium Indian Food</MaskedLine>
          <MaskedLine delay={0.3}>Products, <span className="text-gold">Delivered</span></MaskedLine>
          <MaskedLine delay={0.45}><span className="text-gold">Worldwide.</span></MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-base leading-relaxed text-white/85 md:text-lg"
        >
          RBL EXIM is a trusted Indian exporter of quality food products,
          offering authentic Indian taste, consistent quality and reliable
          supply for international markets.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/#enquire"
            data-testid="hero-quote-button"
            className="group flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-navy transition-colors duration-300 hover:bg-white"
          >
            Request a Quote
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/catalogue"
            data-testid="hero-products-button"
            className="rounded-full border border-white/40 px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-white hover:bg-white/10"
          >
            Explore Products
          </Link>
          <a
            href={waLink(WA_DEFAULT_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="flex items-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-[#1eb857]"
          >
            <WhatsAppIcon />
            Chat With Us
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.1 }}
        data-testid="hero-trust-bar"
        className="absolute inset-x-0 bottom-0 border-t border-white/15 bg-navy/70 backdrop-blur-sm"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/80 md:text-xs">
            Quality • Authenticity • Reliable Supply • Global Trade
          </p>
          <p className="hidden text-[11px] font-medium uppercase tracking-[0.2em] text-gold md:block md:text-xs">
            Delivering Quality, Building Trust Worldwide.
          </p>
        </div>
      </motion.div>
    </section>
  );
}
